import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import FadeIn from "@/app/components/ui/FadeIn";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useStaffClinicListAPI } from "@/app/hooks/api_hooks/clinic/useStaffClinicListAPI";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { setMappedCityId, setMappedClinicData } from "@/app_redux/reducers/slices/ui_states_slice";
import SideGridImage from "@assets/images/SelectCityAndClinicModalImage.png";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";

interface IUserCityAndClinicFormFields {
  city_id: string;
  clinic_id: string;
  clinic_name: string;
}

const VMSSetupPage = () => {
  const { getRouteKey } = useRouter();
  const authUserDisplayName = useAppSelector((state) => state?.auth?.user?.displayName);
  const { clinics, cities } = useAppSelector((state) => state.root.UIStates);
  const dispatch = useAppDispatch();
  const fetchCities = useOrgCitiesListAPI("UI");
  const fetchClinics = useStaffClinicListAPI("UI");
  const configuration = useConfiguration();

  const navigate = useNavigate();

  const formHook = useForm<IUserCityAndClinicFormFields>({
    mode: "onChange",
  });

  /* Select Items Section Starts */
  const citySelectItems = useSelectMenuReducer(cities, "name", "id");
  const clinicSelectItems = useSelectMenuReducer(clinics, "display_name", "id");

  /* Select Items Section Ends */

  useEffect(() => {
    fetchCities({
      withClinics: false,
    });
  }, []);

  useEffect(() => {
    if (formHook.getValues("city_id")) {
      fetchClinics({
        cityIds: [formHook.getValues("city_id")],
        withProviders: false,
      });
      formHook.setValue("clinic_id", null);
      formHook.setValue("clinic_name", null);
    }
  }, [formHook.watch("city_id")]);

  useEffect(() => {
    const mappedClinicId = localStorage.getItem(configuration.localStorage.CITY.key);
    if (mappedClinicId) {
      navigate(getRouteKey("SEARCH_CUSTOMER", "url"));
    }
  }, []);

  const onSubmit = (data: IUserCityAndClinicFormFields) => {
    dispatch(
      setMappedClinicData({
        id: data.clinic_id,
        name: data.clinic_name,
      }),
    );
    dispatch(setMappedCityId(data.city_id));

    localStorage.setItem(configuration.localStorage.CITY.key, data.city_id);
    localStorage.setItem(configuration.localStorage.CLINIC.key, data.clinic_id);
    localStorage.setItem(configuration.localStorage.CLINIC_NAME.key, data.clinic_name);

    navigate(getRouteKey("SEARCH_CUSTOMER", "url"));
  };
  return (
    <div className="flex justify-center items-center flex-col fixed top-0 left-0 z-50 w-screen h-screen bg-gray-50">
      <FadeIn show>
        <div>
          <div className="mb-4 flex justify-start items-center  gap-x-3">
            <p className="font-semibold text-2xl text-gray-700 ">Setup Your Clinic</p>
            <Link to={getRouteKey("SEARCH_CUSTOMER", "url")}>
              <SimpleBadge colorClassName="text-blue-600 bg-blue-50 ring-blue-500/10" label="Skip" />
            </Link>
          </div>
          <div className="flex justify-center items-center bg-white rounded-md px-32 py-4 max-w-4xl ">
            <div className="">
              <div className="w-full h-full flex justify-center items-start gap-y-3 flex-col">
                <p className="text-xl text-left text-gray-600">
                  Hi, <strong>{authUserDisplayName}</strong> 👋
                </p>
                <p className="text-gray-500 text-lg">
                  You need to select your designated <strong>City</strong> and <strong>Clinic</strong> to map the VMS
                  with correctly.{" "}
                </p>
                <div className="mt-4">
                  <form className="flex justify-start items-center gap-x-2" onSubmit={formHook.handleSubmit(onSubmit)}>
                    <div className="w-full flex justify-start items-center gap-x-2">
                      <SearchableSelectMenu
                        errorMessages={[{ message: "City is required", type: "required" }]}
                        showDropdownIcon
                        placeholder="Select City"
                        fieldError={formHook.formState.errors.city_id}
                        register={formHook.register("city_id", { required: true })}
                        showTypedErrors
                        selectItems={citySelectItems}
                        onSelectItem={(item) => {
                          if (item) {
                            formHook.setValue("city_id", item.id);
                          }
                        }}
                        defaultSelected={citySelectItems?.filter((c) => c.id === formHook.watch("city_id"))[0]}
                      />
                      <SearchableSelectMenu
                        errorMessages={[{ message: "Clinic is required", type: "required" }]}
                        showDropdownIcon
                        placeholder="Select Clinic"
                        fieldError={formHook.formState.errors.clinic_id}
                        register={formHook.register("clinic_id", { required: true })}
                        showTypedErrors
                        selectItems={clinicSelectItems}
                        onSelectItem={(item) => {
                          if (item) {
                            formHook.setValue("clinic_id", item.id);
                            formHook.setValue("clinic_name", item.title);
                          }
                        }}
                        defaultSelected={clinicSelectItems?.filter((c) => c.id === formHook.watch("clinic_id"))[0]}
                      />
                    </div>
                    <input
                      type="submit"
                      value={"Use VMS"}
                      className="rounded-md cursor-pointer bg-indigo-600  px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    />
                  </form>
                </div>
              </div>
            </div>
            <div className="col-span-2">
              <img src={SideGridImage} alt="side_grid_image" className="w-full" />
            </div>
          </div>
        </div>
      </FadeIn>
    </div>
  );
};

export default VMSSetupPage;
